"use client";

import ChatBubbleLeftRightIcon from "@heroicons/react/24/solid/ChatBubbleLeftRightIcon";
import CommentCard from "@/components/views/venue-details/card/comments/CommentCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRef, useState, useEffect } from "react";
import { VENUE_TABS } from "@/@core/constants/venue-tabs";
import { useSession } from "next-auth/react";
import dynamic from "next/dynamic";
import { useDataUpdater } from "@/lib/hooks/useApi";
import toast from "react-hot-toast";
import { useTranslation } from "@/hooks/translationContext";
import { useVenueDetail } from "@/hooks/venueContext";

const AddCommentModal = dynamic(
  () =>
    import(
      "@/components/views/venue-details/new-details/comments/AddCommentModal"
    ),
  {},
);

const AddReplyModal = dynamic(() => import("@/components/views/venue-details/new-details/comments/AddReplyModal"))

interface Props {
  venueId: string;
  datas: Venue.Comment[];
  sendCommentDatas: {
    image: string;
    title: string;
    id: number;
  };
}

export default function CommentsCardBody(props: Props) {
  const commentsRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const { status: sessionStatus } = useSession();
  const [showReplyModal, setShowReplyModal] = useState<boolean>(false)
  const [parentComment, setParentComment] = useState<ParentComment>()
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { venue } = useVenueDetail();

  const handleShowReply = (parent) => {
    setParentComment(parent)
    setShowReplyModal(true)
  }

  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#yorum-ekle") {
        setShowModal(true);
      }
    };

    checkHash();
    window.addEventListener("hashchange", checkHash);

    return () => {
      window.removeEventListener("hashchange", checkHash);
    };
  }, []);

  const { sendRequest } = useDataUpdater({
    auth: sessionStatus === "authenticated",
    method: "POST",
    path: sessionStatus === "authenticated" ? "comment" : "comment/guest",
  });

  const handleSubmit = async (data): Promise<boolean | number> => {
    const toastId = toast.loading(t("venue-detail-reply-submit-loading"));
    setIsLoading(true);

    try {
      const bodyData = sessionStatus === "authenticated" ? {
        type: "venue",
        source: "system",
        reference_id: props.venueId,
        text: data.text,
        rating: data.rating,
        parent_id: data.parent_id
      } : {
        type: "venue",
        source: "system",
        reference_id: props.venueId,
        text: data.text,
        rating: data.rating,
        parent_id: data.parent_id,
        fullName: data.fullName,
        ...(data.phone && { phone: data.phone }),
        ...(data.instagram_username && { instagram_username: data.instagram_username }),
        ...(data.address && { address: data.address }),
        ...(data.email && { email: data.email }),
      }

      const res = await sendRequest(bodyData);
      if (res) {
        toast.success(t("venue-detail-reply-pending-approval-info"), {
          id: toastId,
        });
        return typeof res === 'number' ? res : true;
      } else {
        toast.error(t("venue-detail-reply-submit-error"), { id: toastId });
        return false;
      }
    } catch (error) {
      toast.error(t("venue-detail-reply-submit-error"), { id: toastId });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      id={VENUE_TABS.COMMENTS}
      className="flex flex-col mt-6 lg:max-w-[895px] relative border rounded-[32px] px-4 pt-4 pb-2 gap-4"
    >
      <AddCommentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />

      <div className="flex justify-between bg-stone-50 rounded-[16px] py-1.5 px-2">
        <div className="flex items-center gap-2 ">
          <ChatBubbleLeftRightIcon className="size-5 text-primary" />
          <h2 className="font-bold text-sm">
            {t("venue-commentSection-title", {
              venue: venue?.descriptions?.[0]?.title,
              defaultValue: "Yorum/Soru"
            })}
          </h2>
          <div className="py-[2px] cursor-default px-[6px] text-xs bg-primary text-white font-[500] rounded-full">
            {props.datas.length}
          </div>
        </div>
        <div>
          <Button
            onClick={() => {
              setShowModal(true);
            }}
            variant="assistant"
            className={cn(
              "text-[10px] !h-6 flex justify-center items-center text-sm font-bold px-2 py-1",
            )}
          >
           Yorum Yaz/Soru Sor
          </Button>
        </div>
      </div>

      <div
        ref={commentsRef}
        className={cn("grid grid-cols-1 md:grid-cols-2 gap-4 pb-2 items-start")}
      >
        {props.datas.map((comment, index) => {
          return <CommentCard handleShowReply={handleShowReply} key={index} comment={comment} />;
        })}
      </div>

      {parentComment && (
        <AddReplyModal
          isLoading={isLoading}
          handleSubmit={handleSubmit}
          parentComment={parentComment}
          isOpen={showReplyModal}
          onClose={() => setShowReplyModal(false)}
        />
      )}
    </div>
  );
}
